import { useParams, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { mockInvoices, mockInvoiceLines } from '@/mocks/invoices';
import { mockPartners } from '@/mocks/partners';
import { ArrowLeft, FileText, Download, CheckCircle } from 'lucide-react';

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const invoice = useMemo(() => mockInvoices.find(i => i.id === id), [id]);
  const lines = useMemo(() => mockInvoiceLines.filter(l => l.invoice_id === id), [id]);
  const partner = useMemo(() => mockPartners.find(p => p.id === invoice?.partner_id), [invoice]);

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Invoice not found</p>
        <button onClick={() => navigate('/finance/invoices')} className="mt-2 text-sm transition-colors" style={{ color: 'var(--text-muted)' }}>Back</button>
      </div>
    );
  }

  const statusBadge = (status: string) => {
    switch (status) {
      case 'paid': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'sent': return { bg: 'var(--bg-input)', color: 'var(--text-muted)', border: 'var(--border-primary)' };
      case 'draft': return { bg: 'var(--bg-input)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
      case 'overdue': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
      default: return { bg: 'var(--bg-input)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/finance/invoices')}
          className="p-1.5 rounded-lg transition-colors hover-surface"
          style={{ color: 'var(--text-muted)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>{invoice.invoice_number}</h1>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{partner?.display_name}</p>
        </div>
        <span
          className="ml-auto inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border"
          style={statusBadge(invoice.status)}
        >
          {invoice.status}
        </span>
      </div>

      <div className="rounded-xl p-6 space-y-6" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
        {/* Header info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-sm">
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Period</label>
            <p style={{ color: 'var(--text-secondary)' }}>{invoice.period_start} — {invoice.period_end}</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Currency</label>
            <p style={{ color: 'var(--text-secondary)' }}>{invoice.currency}</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'var(--text-muted)' }}>Tax Treatment</label>
            <p style={{ color: 'var(--text-secondary)' }}>{invoice.tax_treatment.replace(/_/g, ' ')}</p>
          </div>
        </div>

        {/* Line items */}
        <div className="border-t pt-5" style={{ borderColor: 'var(--border-separator)' }}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Line Items</h3>
          <div className="overflow-x-auto table-scroll">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-separator)' }}>
                  <th className="text-left py-2.5 font-medium" style={{ color: 'var(--text-muted)' }}>Description</th>
                  <th className="text-right py-2.5 font-medium" style={{ color: 'var(--text-muted)' }}>Local</th>
                  <th className="text-right py-2.5 font-medium" style={{ color: 'var(--text-muted)' }}>EUR</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
                {lines.map(l => (
                  <tr key={l.id} className="transition-colors hover-surface">
                    <td className="py-2.5" style={{ color: 'var(--text-tertiary)' }}>{l.description}</td>
                    <td className="py-2.5 text-right" style={{ color: 'var(--text-tertiary)' }}>{l.amount_local}</td>
                    <td className="py-2.5 text-right font-medium" style={{ color: 'var(--text-secondary)' }}>€{l.amount_eur}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="border-t pt-5 flex justify-end" style={{ borderColor: 'var(--border-separator)' }}>
          <div className="w-full sm:w-64 space-y-2 text-sm">
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>
              <span style={{ color: 'var(--text-secondary)' }}>{invoice.subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-muted)' }}>Tax ({invoice.tax_rate}%)</span>
              <span style={{ color: 'var(--text-secondary)' }}>{invoice.tax_amount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between border-t pt-2" style={{ borderColor: 'var(--border-separator)' }}>
              <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Total</span>
              <span className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{invoice.total.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t" style={{ borderColor: 'var(--border-separator)' }}>
          <button
            onClick={() => { /* PDF generation requires Edge Function */ }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-all hover-surface"
            style={{ color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}
          >
            <Download size={14} />
            PDF
          </button>
          {invoice.status === 'sent' && (
            <button
              onClick={() => { /* Mark paid handler */ }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-xl transition-all"
              style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
            >
              <CheckCircle size={14} />
              Mark Paid
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
